/*
  AminoLabs Firebase auth bridge.
  - Wires the auth modal (sign in / sign up / reset) to Firebase Auth
  - Persists profile + address book to Firestore (users/{uid})
  - Exposes window.aminoAuth for other scripts (cart drawer, account page)
  - Intercepts checkout buttons to require sign in and prefill the Shopify checkout
*/
import { initializeApp } from 'https://www.gstatic.com/firebasejs/10.13.2/firebase-app.js';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'https://www.gstatic.com/firebasejs/10.13.2/firebase-auth.js';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.2/firebase-firestore.js';

const config = window.aminoFirebaseConfig;
const routes = window.aminoAuthRoutes || {
  account: '/pages/account',
  cart: '/cart',
  cartUpdate: '/cart/update',
  checkout: '/checkout'
};

if (!config) {
  console.warn('[auth] Firebase config missing. Did firebase-init.liquid render?');
}

const app = config ? initializeApp(config) : null;
const auth = app ? getAuth(app) : null;
const db = app ? getFirestore(app) : null;
if (auth) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.warn('[auth] persistence fallback:', error?.message);
  });
}

const state = {
  user: null,
  profile: null,
  ready: false,
  listeners: new Set()
};

const notify = () => {
  state.listeners.forEach((listener) => {
    try { listener(state); } catch (error) { console.error(error); }
  });
};

const fetchProfile = async (uid) => {
  if (!db) return null;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.warn('[auth] profile fetch failed:', error?.message);
    return null;
  }
};

if (auth) {
  onAuthStateChanged(auth, async (user) => {
    state.user = user;
    state.profile = user ? await fetchProfile(user.uid) : null;
    state.ready = true;
    document.documentElement.classList.toggle('is-authed', !!user);
    notify();
  });
}

/* ---------- modal controller ---------- */

const modal = document.querySelector('[data-auth-modal]');
const alertBox = modal?.querySelector('[data-auth-alert]');
const forms = {
  signin: modal?.querySelector('[data-auth-form="signin"]'),
  signup: modal?.querySelector('[data-auth-form="signup"]'),
  reset: modal?.querySelector('[data-auth-form="reset"]')
};

let pendingRedirect = null;
let lastFocused = null;

const showAlert = (message, tone = 'error') => {
  if (!alertBox) return;
  alertBox.textContent = message;
  alertBox.dataset.tone = tone;
  alertBox.hidden = !message;
};

const setActiveTab = (tab) => {
  if (!modal) return;
  modal.querySelectorAll('[data-auth-tab]').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.authTab === tab);
  });
  Object.entries(forms).forEach(([key, form]) => {
    if (form) form.hidden = key !== tab;
  });
  const tabsBar = modal.querySelector('[data-auth-tabs]');
  if (tabsBar) tabsBar.hidden = tab === 'reset';
  showAlert('');
};

const openModal = (tab = 'signin') => {
  if (!modal) return;
  lastFocused = document.activeElement;
  modal.hidden = false;
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('auth-modal-open');
  requestAnimationFrame(() => {
    modal.classList.add('is-open');
    setActiveTab(tab);
    const focusable = forms[tab]?.querySelector('input, select, button');
    focusable?.focus();
  });
};

const closeModal = () => {
  if (!modal) return;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('auth-modal-open');
  showAlert('');
  pendingRedirect = null;
  window.setTimeout(() => {
    if (!modal.classList.contains('is-open')) modal.hidden = true;
  }, 220);
  lastFocused?.focus?.();
};

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-auth-open]');
  if (opener) {
    event.preventDefault();
    pendingRedirect = opener.dataset.authRedirect || null;
    openModal(opener.dataset.authTab || 'signin');
    return;
  }
  if (event.target.closest('[data-auth-close]')) {
    event.preventDefault();
    closeModal();
    return;
  }
  const tab = event.target.closest('[data-auth-tab]');
  if (tab && modal && !modal.hidden) {
    event.preventDefault();
    setActiveTab(tab.dataset.authTab);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
});

const setSubmitting = (form, submitting) => {
  const button = form?.querySelector('[data-auth-submit]');
  if (!button) return;
  if (!button.dataset.defaultText) button.dataset.defaultText = button.textContent;
  button.disabled = submitting;
  button.textContent = submitting ? 'Working…' : button.dataset.defaultText;
};

const friendlyError = (error) => {
  const code = error?.code || '';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'That email or password is incorrect.';
  }
  if (code === 'auth/email-already-in-use') return 'An account with that email already exists. Try signing in.';
  if (code === 'auth/weak-password') return 'Password must be at least 8 characters.';
  if (code === 'auth/invalid-email') return 'That email address looks invalid.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Please wait a minute and try again.';
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection and try again.';
  return error?.message || 'Something went wrong. Please try again.';
};

const handleSuccess = async () => {
  if (pendingRedirect) {
    const target = pendingRedirect;
    pendingRedirect = null;
    closeModal();
    if (target === 'checkout') {
      await proceedToCheckout({ silent: true });
    } else {
      window.location.href = target;
    }
    return;
  }
  closeModal();
};

forms.signin?.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (!auth) return;
  const form = event.currentTarget;
  const email = form.email.value.trim();
  const password = form.password.value;
  if (!email || !password) return;
  setSubmitting(form, true);
  showAlert('');
  try {
    await signInWithEmailAndPassword(auth, email, password);
    await handleSuccess();
  } catch (error) {
    showAlert(friendlyError(error));
  } finally {
    setSubmitting(form, false);
  }
});

forms.signup?.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (!auth || !db) return;
  const form = event.currentTarget;
  const data = Object.fromEntries(new FormData(form).entries());
  if (!data.ageConfirm || !data.researchConfirm) {
    showAlert('Please confirm both age and research-use boxes.');
    return;
  }
  setSubmitting(form, true);
  showAlert('');
  try {
    const credential = await createUserWithEmailAndPassword(auth, data.email.trim(), data.password);
    const fullName = `${data.firstName} ${data.lastName}`.trim();
    if (credential.user && fullName) {
      try { await updateProfile(credential.user, { displayName: fullName }); } catch (_) {}
    }
    const profile = {
      email: data.email.trim(),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      phone: data.phone.trim(),
      address: {
        address1: data.address1.trim(),
        address2: (data.address2 || '').trim(),
        city: data.city.trim(),
        province: data.province.trim().toUpperCase(),
        zip: data.zip.trim(),
        country: data.country.trim().toUpperCase()
      },
      ageVerified: true,
      researchVerified: true,
      shopifyCustomerId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    await setDoc(doc(db, 'users', credential.user.uid), profile);
    state.profile = profile;
    await handleSuccess();
  } catch (error) {
    showAlert(friendlyError(error));
  } finally {
    setSubmitting(form, false);
  }
});

forms.reset?.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (!auth) return;
  const form = event.currentTarget;
  const email = form.email.value.trim();
  if (!email) return;
  setSubmitting(form, true);
  showAlert('');
  try {
    await sendPasswordResetEmail(auth, email);
    showAlert('If an account exists for that email, a reset link has been sent.', 'success');
  } catch (error) {
    showAlert(friendlyError(error));
  } finally {
    setSubmitting(form, false);
  }
});

/* ---------- checkout intercept ---------- */

const buildCheckoutUrl = (profile) => {
  if (!profile) return routes.checkout;
  const params = new URLSearchParams();
  const a = profile.address || {};
  const set = (key, value) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value).trim());
    }
  };
  set('checkout[email]', profile.email);
  set('checkout[shipping_address][first_name]', profile.firstName);
  set('checkout[shipping_address][last_name]', profile.lastName);
  set('checkout[shipping_address][address1]', a.address1);
  set('checkout[shipping_address][address2]', a.address2);
  set('checkout[shipping_address][city]', a.city);
  set('checkout[shipping_address][province]', a.province);
  set('checkout[shipping_address][zip]', a.zip);
  set('checkout[shipping_address][country]', a.country);
  set('checkout[shipping_address][phone]', profile.phone);
  return `${routes.checkout}?${params.toString()}`;
};

const tagCartWithVerification = async (profile) => {
  try {
    const body = new URLSearchParams();
    body.set('attributes[Research verification status]', 'Confirmed');
    body.set('attributes[Research verification email]', profile.email || '');
    body.set('attributes[Research verification name]', `${profile.firstName || ''} ${profile.lastName || ''}`.trim());
    body.set('attributes[Phone]', profile.phone || '');
    body.set('attributes[Research use confirmation]', 'Confirmed research use only');
    body.set('attributes[Age confirmation]', '18+');
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: body.toString()
    });
  } catch (error) {
    console.warn('[auth] cart attribute update failed:', error?.message);
  }
};

const proceedToCheckout = async ({ silent = false } = {}) => {
  if (!state.user) {
    pendingRedirect = 'checkout';
    openModal('signin');
    if (!silent) showAlert('Please sign in or create an account to continue.', 'info');
    return;
  }
  if (!state.profile) {
    state.profile = await fetchProfile(state.user.uid);
  }
  if (!state.profile) {
    showAlert('We could not load your saved details. Please refresh and try again.');
    openModal('signin');
    return;
  }
  await tagCartWithVerification(state.profile);
  window.location.href = buildCheckoutUrl(state.profile);
};

document.addEventListener('click', (event) => {
  const checkoutTrigger = event.target.closest('[data-auth-checkout]');
  if (!checkoutTrigger) return;
  event.preventDefault();
  proceedToCheckout();
});

/* ---------- account-page bindings ---------- */

const bindAccountPage = () => {
  const root = document.querySelector('[data-account-root]');
  if (!root) return;

  const signOutButton = root.querySelector('[data-account-signout]');
  signOutButton?.addEventListener('click', async () => {
    if (!auth) return;
    await signOut(auth);
    window.location.reload();
  });

  const profileForm = root.querySelector('[data-account-profile-form]');
  profileForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!state.user || !db) return;
    const data = Object.fromEntries(new FormData(profileForm).entries());
    const status = profileForm.querySelector('[data-account-status]');
    const button = profileForm.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    if (status) { status.textContent = 'Saving…'; status.dataset.tone = ''; }
    try {
      const update = {
        firstName: data.firstName?.trim() || '',
        lastName: data.lastName?.trim() || '',
        phone: data.phone?.trim() || '',
        address: {
          address1: data.address1?.trim() || '',
          address2: data.address2?.trim() || '',
          city: data.city?.trim() || '',
          province: (data.province || '').trim().toUpperCase(),
          zip: data.zip?.trim() || '',
          country: (data.country || 'US').trim().toUpperCase()
        },
        updatedAt: serverTimestamp()
      };
      await updateDoc(doc(db, 'users', state.user.uid), update);
      state.profile = { ...(state.profile || {}), ...update };
      if (status) { status.textContent = 'Saved.'; status.dataset.tone = 'success'; }
      notify();
    } catch (error) {
      if (status) { status.textContent = friendlyError(error); status.dataset.tone = 'error'; }
    } finally {
      if (button) button.disabled = false;
    }
  });

  const renderAccount = () => {
    const loading = root.querySelector('[data-account-loading]');
    const guest = root.querySelector('[data-account-guest]');
    const member = root.querySelector('[data-account-member]');
    if (!state.ready) {
      if (loading) loading.hidden = false;
      if (guest) guest.hidden = true;
      if (member) member.hidden = true;
      return;
    }
    if (loading) loading.hidden = true;
    if (!state.user) {
      if (guest) guest.hidden = false;
      if (member) member.hidden = true;
      return;
    }
    if (guest) guest.hidden = true;
    if (member) member.hidden = false;
    const profile = state.profile || {};
    const a = profile.address || {};
    const setVal = (name, value) => {
      const el = profileForm?.elements?.[name];
      if (el && value !== undefined) el.value = value || '';
    };
    setVal('firstName', profile.firstName);
    setVal('lastName', profile.lastName);
    setVal('phone', profile.phone);
    setVal('address1', a.address1);
    setVal('address2', a.address2);
    setVal('city', a.city);
    setVal('province', a.province);
    setVal('zip', a.zip);
    setVal('country', a.country || 'US');
    const emailEl = root.querySelector('[data-account-email]');
    if (emailEl) emailEl.textContent = profile.email || state.user.email || '';
    const nameEl = root.querySelector('[data-account-name]');
    if (nameEl) {
      const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      nameEl.textContent = fullName || profile.email || state.user.email || 'Account';
    }
  };

  state.listeners.add(renderAccount);
  renderAccount();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindAccountPage);
} else {
  bindAccountPage();
}

/* ---------- public surface ---------- */


/* ---------- inline checkout-page bindings ---------- */

const bindCheckoutPage = () => {
  const root = document.querySelector('[data-checkout-root]');
  if (!root) return;

  const loadingEl = root.querySelector('[data-checkout-loading]');
  const stepEls = {
    account: root.querySelector('[data-checkout-step="account"]'),
    shipping: root.querySelector('[data-checkout-step="shipping"]')
  };
  const indicators = {
    account: root.querySelector('[data-step-indicator="account"]'),
    shipping: root.querySelector('[data-step-indicator="shipping"]')
  };
  const forms = {
    signin: root.querySelector('[data-checkout-form="signin"]'),
    signup: root.querySelector('[data-checkout-form="signup"]'),
    shipping: root.querySelector('[data-checkout-form="shipping"]')
  };
  const accountAlert = root.querySelector('[data-checkout-alert]');
  const shippingAlert = root.querySelector('[data-checkout-shipping-alert]');
  const emailLabel = root.querySelector('[data-checkout-email]');
  const savedView = root.querySelector('[data-checkout-saved]');
  const savedName = root.querySelector('[data-checkout-saved-name]');
  const savedAddress = root.querySelector('[data-checkout-saved-address]');
  const savedPhone = root.querySelector('[data-checkout-saved-phone]');

  const setAlert = (el, message, tone = 'error') => {
    if (!el) return;
    el.textContent = message;
    el.dataset.tone = tone;
    el.hidden = !message;
  };

  const setStep = (name) => {
    if (loadingEl) loadingEl.hidden = true;
    Object.entries(stepEls).forEach(([key, el]) => {
      if (el) el.hidden = key !== name;
    });
    Object.entries(indicators).forEach(([key, el]) => {
      if (!el) return;
      el.classList.toggle('is-active', key === name);
      const order = ['account', 'shipping'];
      el.classList.toggle('is-complete', order.indexOf(key) < order.indexOf(name));
    });
  };

  const switchAccountTab = (tab) => {
    root.querySelectorAll('[data-checkout-toggle]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.checkoutToggle === tab);
    });
    if (forms.signup) forms.signup.hidden = tab !== 'signup';
    if (forms.signin) forms.signin.hidden = tab !== 'signin';
    setAlert(accountAlert, '');
  };

  root.querySelectorAll('[data-checkout-toggle]').forEach((button) => {
    button.addEventListener('click', () => switchAccountTab(button.dataset.checkoutToggle));
  });

  const populateShippingForm = (profile = {}) => {
    if (!forms.shipping) return;
    const a = profile.address || {};
    const setVal = (name, value) => {
      const el = forms.shipping.elements?.[name];
      if (el && value !== undefined) el.value = value || '';
    };
    setVal('firstName', profile.firstName);
    setVal('lastName', profile.lastName);
    setVal('phone', profile.phone);
    setVal('address1', a.address1);
    setVal('address2', a.address2);
    setVal('city', a.city);
    setVal('province', a.province);
    setVal('zip', a.zip);
    setVal('country', a.country || 'US');
    if (emailLabel) emailLabel.textContent = profile.email || state.user?.email || '';
  };

  const renderSavedView = (profile = {}) => {
    const a = profile.address || {};
    const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    if (savedName) savedName.textContent = fullName || profile.email || state.user?.email || 'No name on file';
    if (savedAddress) {
      const parts = [];
      if (a.address1) parts.push(a.address1);
      if (a.address2) parts.push(a.address2);
      const cityLine = [a.city, a.province, a.zip].filter(Boolean).join(', ');
      if (cityLine) parts.push(cityLine);
      if (a.country) parts.push(a.country);
      savedAddress.textContent = parts.length ? parts.join(' · ') : 'No address on file — please add one.';
    }
    if (savedPhone) savedPhone.textContent = profile.phone ? `Phone: ${profile.phone}` : '';
    if (emailLabel) emailLabel.textContent = profile.email || state.user?.email || '';
  };

  const hasUsableAddress = (profile = {}) => {
    const a = profile.address || {};
    return Boolean(a.address1 && a.city && a.zip && a.country);
  };

  const showEditView = () => {
    if (savedView) savedView.hidden = true;
    if (forms.shipping) forms.shipping.hidden = false;
    setAlert(shippingAlert, '');
  };

  const showSavedView = () => {
    if (savedView) savedView.hidden = false;
    if (forms.shipping) forms.shipping.hidden = true;
    setAlert(shippingAlert, '');
  };

  root.querySelector('[data-checkout-edit]')?.addEventListener('click', () => {
    populateShippingForm(state.profile || {});
    showEditView();
  });

  root.querySelector('[data-checkout-cancel-edit]')?.addEventListener('click', () => {
    showSavedView();
  });

  root.querySelector('[data-checkout-confirm]')?.addEventListener('click', async () => {
    if (!state.user || !state.profile) return;
    if (!hasUsableAddress(state.profile)) {
      showEditView();
      setAlert(shippingAlert, 'Please add a shipping address before continuing.', 'info');
      return;
    }
    const button = root.querySelector('[data-checkout-confirm]');
    if (button) {
      if (!button.dataset.defaultText) button.dataset.defaultText = button.textContent;
      button.disabled = true;
      button.textContent = 'Working…';
    }
    try {
      await tagCartWithVerification(state.profile);
      window.location.href = buildCheckoutUrl(state.profile);
    } catch (error) {
      setAlert(shippingAlert, friendlyError(error));
      if (button) {
        button.disabled = false;
        button.textContent = button.dataset.defaultText;
      }
    }
  });

  const setSubmittingBtn = (form, submitting) => {
    const button = form?.querySelector('[data-checkout-submit]');
    if (!button) return;
    if (!button.dataset.defaultText) button.dataset.defaultText = button.textContent;
    button.disabled = submitting;
    button.textContent = submitting ? 'Working…' : button.dataset.defaultText;
  };

  /* signup */
  forms.signup?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!auth || !db) return;
    const data = Object.fromEntries(new FormData(forms.signup).entries());
    if (!data.ageConfirm || !data.researchConfirm) {
      setAlert(accountAlert, 'Please confirm both age and research-use boxes.');
      return;
    }
    setSubmittingBtn(forms.signup, true);
    setAlert(accountAlert, '');
    try {
      const credential = await createUserWithEmailAndPassword(auth, data.email.trim(), data.password);
      const fullName = `${data.firstName} ${data.lastName}`.trim();
      if (credential.user && fullName) {
        try { await updateProfile(credential.user, { displayName: fullName }); } catch (_) {}
      }
      const profile = {
        email: data.email.trim(),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phone: data.phone.trim(),
        address: {
          address1: data.address1.trim(),
          address2: (data.address2 || '').trim(),
          city: data.city.trim(),
          province: data.province.trim().toUpperCase(),
          zip: data.zip.trim(),
          country: data.country.trim().toUpperCase()
        },
        ageVerified: true,
        researchVerified: true,
        shopifyCustomerId: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await setDoc(doc(db, 'users', credential.user.uid), profile);
      state.profile = profile;
      populateShippingForm(profile);
      setStep('shipping');
    } catch (error) {
      setAlert(accountAlert, friendlyError(error));
    } finally {
      setSubmittingBtn(forms.signup, false);
    }
  });

  /* signin */
  forms.signin?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!auth) return;
    const email = forms.signin.email.value.trim();
    const password = forms.signin.password.value;
    if (!email || !password) return;
    setSubmittingBtn(forms.signin, true);
    setAlert(accountAlert, '');
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const profile = await fetchProfile(credential.user.uid);
      state.profile = profile;
      if (profile) populateShippingForm(profile);
      setStep('shipping');
    } catch (error) {
      setAlert(accountAlert, friendlyError(error));
    } finally {
      setSubmittingBtn(forms.signin, false);
    }
  });

  /* forgot password — opens the reset tab in the modal */
  root.querySelector('[data-checkout-reset]')?.addEventListener('click', () => {
    pendingRedirect = null;
    openModal('reset');
  });

  /* sign out from the shipping step */
  root.querySelector('[data-checkout-signout]')?.addEventListener('click', async () => {
    if (!auth) return;
    await signOut(auth);
    setStep('account');
    switchAccountTab('signup');
  });

  /* shipping submit -> save profile, tag cart, redirect to Shopify checkout */
  forms.shipping?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!state.user || !db) {
      setAlert(shippingAlert, 'Your session expired. Please sign in again.');
      setStep('account');
      switchAccountTab('signin');
      return;
    }
    const data = Object.fromEntries(new FormData(forms.shipping).entries());
    setSubmittingBtn(forms.shipping, true);
    setAlert(shippingAlert, '');
    try {
      const update = {
        firstName: data.firstName?.trim() || '',
        lastName: data.lastName?.trim() || '',
        phone: data.phone?.trim() || '',
        address: {
          address1: data.address1?.trim() || '',
          address2: data.address2?.trim() || '',
          city: data.city?.trim() || '',
          province: (data.province || '').trim().toUpperCase(),
          zip: data.zip?.trim() || '',
          country: (data.country || 'US').trim().toUpperCase()
        },
        updatedAt: serverTimestamp()
      };
      await updateDoc(doc(db, 'users', state.user.uid), update);
      const profile = {
        ...(state.profile || {}),
        email: state.profile?.email || state.user.email,
        ...update
      };
      state.profile = profile;
      renderSavedView(profile);
      showSavedView();
      await tagCartWithVerification(profile);
      window.location.href = buildCheckoutUrl(profile);
    } catch (error) {
      setAlert(shippingAlert, friendlyError(error));
      setSubmittingBtn(forms.shipping, false);
    }
  });

  /* react to auth state */
  const renderCheckout = async () => {
    if (!state.ready) {
      if (loadingEl) loadingEl.hidden = false;
      Object.values(stepEls).forEach((el) => { if (el) el.hidden = true; });
      return;
    }
    if (!state.user) {
      setStep('account');
      switchAccountTab('signup');
      return;
    }
    if (!state.profile) {
      state.profile = await fetchProfile(state.user.uid);
    }
    const profile = state.profile || {};
    populateShippingForm(profile);
    renderSavedView(profile);
    if (hasUsableAddress(profile)) {
      showSavedView();
    } else {
      showEditView();
    }
    setStep('shipping');
  };

  state.listeners.add(renderCheckout);
  renderCheckout();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindCheckoutPage);
} else {
  bindCheckoutPage();
}

window.aminoAuth = {
  open: openModal,
  close: closeModal,
  signOut: () => auth && signOut(auth),
  getState: () => ({ ...state }),
  subscribe: (listener) => {
    state.listeners.add(listener);
    listener(state);
    return () => state.listeners.delete(listener);
  },
  proceedToCheckout
};
